import { ChevronDown, UserCheck } from "lucide-react";
import { assignmentActionsFor, canViewLead, resolveViewerScope } from "@school-sales-ai/shared";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AssigneeMenuItems } from "@/components/lead-assignment/AssigneeMenuItems";
import { hasAssignmentActions } from "@/lib/lead-assignment";
import { useChangeAssignee, useLeadAssignmentPolicy } from "@/hooks/useLeadAssignment";
import { useCurrentUserProfile } from "@/hooks/useUsers";
import { cn } from "@/lib/utils";

type Props = {
  leadId: string;
  assignedProfileId: string | null;
  /**
   * Called after a change that takes the lead out of THIS user's own scope (a
   * member transferring it away under auto-assignment, say) — the page should
   * leave, since the lead's next fetch will 404.
   */
  onLeftScope?: () => void;
  className?: string;
};

/**
 * «Asignado a» on the lead page: the current owner, and whatever this user may
 * do about it. The actions offered come from `assignmentActionsFor` in
 * packages/shared — the same rules the endpoint enforces — so a button the
 * server would refuse is never shown. Renders nothing while the client's
 * assignment policy is off.
 */
export function AssigneeControl({
  leadId,
  assignedProfileId,
  onLeftScope,
  className,
}: Props): React.JSX.Element | null {
  const { data: policy } = useLeadAssignmentPolicy();
  const profile = useCurrentUserProfile();
  const change = useChangeAssignee(leadId);

  if (!policy?.enabled || !profile) return null;

  const actions = assignmentActionsFor(policy, profile, assignedProfileId);
  const assignee = policy.members.find((m) => m.id === assignedProfileId) ?? null;
  const label = assignee ? assignee.name : "Sin asignar";
  const hasActions = hasAssignmentActions(actions);

  function select(profileId: string | null): void {
    if (!profile || !policy) return;
    change.mutate(profileId, {
      onSuccess: () => {
        const scope = resolveViewerScope(policy, profile);
        if (!canViewLead(scope, profileId)) onLeftScope?.();
      },
    });
  }

  const chip = (
    <span className="inline-flex items-center gap-1.5">
      <UserCheck size={14} className="shrink-0 text-ink-400" />
      <span className="truncate">
        <span className="text-ink-400">Asignado a: </span>
        <span className={assignee ? "text-ink-900" : "italic text-ink-500"}>{label}</span>
      </span>
    </span>
  );

  const chipClass = cn(
    "inline-flex max-w-[240px] items-center gap-1 rounded-lg border border-ink-200 px-2.5 py-1.5 text-xs font-medium",
    className,
  );

  if (!hasActions) {
    return (
      <span className={chipClass} title={label}>
        {chip}
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={change.isPending}
          className={cn(chipClass, "hover:bg-ink-050 transition-colors disabled:opacity-50")}
          aria-label={`Asignado a: ${label}`}
        >
          {chip}
          <ChevronDown size={12} className="shrink-0 text-ink-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <AssigneeMenuItems
          actions={actions}
          members={policy.members}
          viewerId={profile.id}
          assignedProfileId={assignedProfileId}
          onSelect={select}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
